import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import OscarImporter from "../components/OscarImporter";
import { 
  FileEdit, 
  Settings, 
  Wrench, 
  Eye, 
  MoreHorizontal,
  Plus,
  ClipboardList,
  Calendar,
  User,
  Loader2,
  Bot,
  Send,
  Sparkles,
  Check,
  X,
  AlertTriangle,
  DollarSign,
  ToggleLeft,
  CalendarPlus,
  ExternalLink,
  Copy,
  CheckCheck,
  Download
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// URL de Oscar PMS para abrir directamente
const OSCAR_URL = "https://oscar.redforts.com";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

const CATEGORIES = [
  { value: "precio", label: "Precio modificado", icon: FileEdit, color: "bg-blue-500" },
  { value: "configuracion", label: "Configuración cambiada", icon: Settings, color: "bg-purple-500" },
  { value: "correccion", label: "Error corregido", icon: Wrench, color: "bg-orange-500" },
  { value: "observacion", label: "Observación", icon: Eye, color: "bg-green-500" },
  { value: "otro", label: "Otro", icon: MoreHorizontal, color: "bg-gray-500" },
];

const getCategoryInfo = (category) => {
  return CATEGORIES.find(c => c.value === category) || CATEGORIES[4];
};

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const ChangeLogEntry = ({ entry }) => {
  const categoryInfo = getCategoryInfo(entry.category);
  const Icon = categoryInfo.icon;

  return (
    <div className="flex gap-4 p-4 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
      <div className={`w-10 h-10 rounded-full ${categoryInfo.color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <Badge variant="secondary" className="text-xs">
            {categoryInfo.label}
          </Badge>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(entry.created_at)}
          </span>
        </div>
        <p className="text-sm text-foreground whitespace-pre-wrap break-words">
          {entry.description}
        </p>
        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
          <User className="w-3 h-3" />
          <span>{entry.user_name}</span>
        </div>
      </div>
    </div>
  );
};

const ChatMessage = ({ message, isUser }) => (
  <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-primary' : 'bg-gradient-to-br from-purple-500 to-blue-500'}`}>
      {isUser ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
    </div>
    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
      <p className="text-sm whitespace-pre-wrap">{message}</p>
    </div>
  </div>
);

// Proposal Card Component
const ProposalCard = ({ proposal, onApply, onCancel, applying, onOpenOscar }) => {
  const [copied, setCopied] = useState(false);
  const icons = {
    price: DollarSign,
    rule: ToggleLeft,
    event: CalendarPlus
  };
  const Icon = icons[proposal.type] || AlertTriangle;
  
  const colors = {
    price: "border-blue-500 bg-blue-500/10",
    rule: "border-purple-500 bg-purple-500/10",
    event: "border-green-500 bg-green-500/10"
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`border-2 rounded-lg p-4 ${colors[proposal.type] || 'border-yellow-500 bg-yellow-500/10'}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
            Propuesta de Cambio
          </h4>
          <p className="text-sm mt-1">{proposal.description}</p>
          {proposal.details && (
            <div className="mt-2 text-xs bg-white/50 rounded p-2">
              {Object.entries(proposal.details).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-muted-foreground">{key}:</span>
                  <span className="font-medium flex items-center gap-1">
                    {value}
                    {key === "Nuevo precio" && (
                      <button 
                        onClick={() => copyToClipboard(value.replace('€', ''))}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Copiar precio"
                      >
                        {copied ? <CheckCheck className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-3 flex-wrap">
            {proposal.type === "price" && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => window.open(OSCAR_URL, '_blank')}
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Abrir Oscar
              </Button>
            )}
            <Button 
              size="sm" 
              onClick={() => onApply(proposal)}
              disabled={applying}
              className="bg-green-600 hover:bg-green-700"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Guardar Nota
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => onCancel(proposal)}
              disabled={applying}
            >
              <X className="w-4 h-4 mr-1" />
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChangeLog = () => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const { toast } = useToast();

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([
    { text: "¡Hola! Soy tu asistente de Revenue Management. Puedo ayudarte a:\n\n• **Revisar** ocupación, precios y eventos\n• **Modificar** precios de habitaciones\n• **Activar/desactivar** reglas de pricing\n• **Crear** nuevos eventos\n\n⚠️ Siempre te pediré confirmación antes de hacer cambios.\n\n¿En qué puedo ayudarte?", isUser: false }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [applyingProposal, setApplyingProposal] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [rules, setRules] = useState([]);
  const chatEndRef = useRef(null);

  // Quick action states
  const [showQuickPrice, setShowQuickPrice] = useState(false);
  const [showQuickRule, setShowQuickRule] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [selectedRule, setSelectedRule] = useState("");

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, proposals]);

  const fetchData = useCallback(async () => {
    try {
      const [entriesRes, statsRes, roomsRes, rulesRes] = await Promise.all([
        axios.get(`${API}/api/changelog`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/changelog/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/assistant/rooms-list`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/assistant/rules-list`, { headers: getAuthHeaders() })
      ]);
      setEntries(entriesRes.data.entries || []);
      setStats(statsRes.data);
      setRooms(roomsRes.data.rooms || []);
      setRules(rulesRes.data.rules || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "No se pudo cargar los datos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!category) {
      toast({ title: "Error", description: "Selecciona una categoría", variant: "destructive" });
      return;
    }

    if (!description.trim()) {
      toast({ title: "Error", description: "Escribe una descripción", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/changelog`, { category, description }, { headers: getAuthHeaders() });
      toast({ title: "Nota guardada", description: "Se ha registrado el cambio correctamente" });
      setCategory("");
      setDescription("");
      fetchData();
    } catch (error) {
      console.error("Error creating entry:", error);
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo guardar la nota", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { text: userMessage, isUser: true }]);
    setChatLoading(true);

    try {
      const response = await axios.post(
        `${API}/api/assistant/chat`,
        { message: userMessage },
        { headers: getAuthHeaders() }
      );
      
      setChatMessages(prev => [...prev, { text: response.data.response, isUser: false }]);
      fetchData();
    } catch (error) {
      console.error("Error chatting with assistant:", error);
      setChatMessages(prev => [...prev, { 
        text: "Lo siento, hubo un error al procesar tu consulta. Por favor, intenta de nuevo.", 
        isUser: false 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleApplyPriceChange = async () => {
    if (!selectedRoom || !newPrice) {
      toast({ title: "Error", description: "Selecciona habitación y precio", variant: "destructive" });
      return;
    }

    const room = rooms.find(r => r.id === selectedRoom);
    const proposal = {
      type: "price",
      description: `Cambiar precio de "${room?.name}" a €${newPrice}`,
      details: {
        "Habitación": room?.name,
        "Precio actual": `€${room?.current_price || room?.base_price}`,
        "Nuevo precio": `€${newPrice}`
      },
      data: { room_id: selectedRoom, new_price: parseFloat(newPrice), reason: "Ajuste manual solicitado por usuario" }
    };
    
    setProposals(prev => [...prev, proposal]);
    setShowQuickPrice(false);
    setSelectedRoom("");
    setNewPrice("");
    
    setChatMessages(prev => [...prev, { 
      text: `He preparado una propuesta para cambiar el precio. Por favor, revísala abajo y confirma si quieres aplicarla.`, 
      isUser: false 
    }]);
  };

  const handleApplyRuleToggle = async () => {
    if (!selectedRule) {
      toast({ title: "Error", description: "Selecciona una regla", variant: "destructive" });
      return;
    }

    const rule = rules.find(r => r.id === selectedRule);
    const newStatus = !rule?.is_active;
    const proposal = {
      type: "rule",
      description: `${newStatus ? "Activar" : "Desactivar"} la regla "${rule?.name}"`,
      details: {
        "Regla": rule?.name,
        "Estado actual": rule?.is_active ? "Activa" : "Inactiva",
        "Nuevo estado": newStatus ? "Activa" : "Inactiva"
      },
      data: { rule_id: selectedRule, activate: newStatus, reason: "Cambio solicitado por usuario" }
    };
    
    setProposals(prev => [...prev, proposal]);
    setShowQuickRule(false);
    setSelectedRule("");
    
    setChatMessages(prev => [...prev, { 
      text: `He preparado una propuesta para ${newStatus ? "activar" : "desactivar"} la regla. Por favor, revísala abajo y confirma.`, 
      isUser: false 
    }]);
  };

  const applyProposal = async (proposal) => {
    setApplyingProposal(true);
    try {
      // For price changes, we just log it (user applies manually in Oscar)
      if (proposal.type === "price") {
        // Log the change as applied
        await axios.post(`${API}/api/changelog`, {
          category: "precio",
          description: `Cambio aplicado en Oscar: ${proposal.description}. ${proposal.data.reason || ''}`
        }, { headers: getAuthHeaders() });
        
        toast({ title: "✅ Registrado", description: "Cambio registrado. Recuerda aplicarlo en Oscar." });
        setProposals(prev => prev.filter(p => p !== proposal));
        
        setChatMessages(prev => [...prev, { 
          text: `✅ Registrado: ${proposal.description}\n\n📝 Recuerda aplicar este cambio en Oscar.`, 
          isUser: false 
        }]);
      } else {
        // For rules and events, apply directly in our system
        let endpoint = "";
        if (proposal.type === "rule") {
          endpoint = "/api/assistant/apply-rule-toggle";
        } else if (proposal.type === "event") {
          endpoint = "/api/assistant/apply-event-create";
        }

        const response = await axios.post(`${API}${endpoint}`, proposal.data, { headers: getAuthHeaders() });
        
        toast({ title: "✅ Cambio aplicado", description: response.data.message });
        setProposals(prev => prev.filter(p => p !== proposal));
        
        setChatMessages(prev => [...prev, { 
          text: `✅ Hecho: ${response.data.message}`, 
          isUser: false 
        }]);
      }
      
      fetchData();
    } catch (error) {
      console.error("Error applying proposal:", error);
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo aplicar el cambio", variant: "destructive" });
    } finally {
      setApplyingProposal(false);
    }
  };

  const cancelProposal = (proposal) => {
    setProposals(prev => prev.filter(p => p !== proposal));
    setChatMessages(prev => [...prev, { 
      text: `❌ Propuesta cancelada: ${proposal.description}`, 
      isUser: false 
    }]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Registro de Cambios</h1>
          <p className="text-muted-foreground">
            Documenta modificaciones y gestiona el sistema con el asistente IA
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/api/reports/changelog/excel`, {
                  headers: getAuthHeaders(),
                  responseType: "blob",
                });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `changelog_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast({ title: "Excel descargado correctamente" });
              } catch (e) {
                toast({ title: "Error al exportar Excel", variant: "destructive" });
              }
            }}
            data-testid="export-changelog-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/api/reports/changelog/pdf`, {
                  headers: getAuthHeaders(),
                  responseType: "blob",
                });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `changelog_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast({ title: "PDF descargado correctamente" });
              } catch (e) {
                toast({ title: "Error al exportar PDF", variant: "destructive" });
              }
            }}
            data-testid="export-changelog-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="col-span-2 md:col-span-1">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{stats.total_entries}</div>
                <p className="text-xs text-muted-foreground">Total Notas</p>
              </div>
            </CardContent>
          </Card>
          {CATEGORIES.slice(0, 4).map(cat => (
            <Card key={cat.value}>
              <CardContent className="pt-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{stats.by_category?.[cat.value] || 0}</div>
                  <p className="text-xs text-muted-foreground truncate">{cat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="assistant" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="assistant" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Asistente IA
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Notas Manuales
          </TabsTrigger>
          <TabsTrigger value="import" className="flex items-center gap-2" data-testid="tab-import-oscar">
            <Download className="w-4 h-4" />
            Importar Oscar
          </TabsTrigger>
        </TabsList>

        {/* AI Assistant Tab */}
        <TabsContent value="assistant">
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  Asistente de Revenue Management
                </CardTitle>
                <CardDescription>
                  Consulta y modifica configuración. Siempre pedirá confirmación.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {/* Chat Messages */}
                <div className="h-[350px] overflow-y-auto p-4 space-y-4 border-y border-border">
                  {chatMessages.map((msg, idx) => (
                    <ChatMessage key={`msg-${idx}-${msg.isUser ? 'user' : 'bot'}`} message={msg.text} isUser={msg.isUser} />
                  ))}
                  {chatLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-muted rounded-2xl px-4 py-3">
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Pending Proposals */}
                {proposals.length > 0 && (
                  <div className="p-4 border-b border-border space-y-3 bg-yellow-50/50">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      Propuestas pendientes de confirmación ({proposals.length})
                    </h4>
                    {proposals.map((proposal, idx) => (
                      <ProposalCard 
                        key={`proposal-${proposal.type}-${idx}`} 
                        proposal={proposal} 
                        onApply={applyProposal}
                        onCancel={cancelProposal}
                        applying={applyingProposal}
                      />
                    ))}
                  </div>
                )}

                {/* Chat Input */}
                <form onSubmit={handleChatSubmit} className="p-4">
                  <div className="flex gap-2">
                    <Textarea
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Escribe tu consulta o pide un cambio..."
                      rows={2}
                      className="resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleChatSubmit(e);
                        }
                      }}
                    />
                    <Button type="submit" disabled={chatLoading || !chatInput.trim()} className="px-4">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
                  <CardDescription>Cambios con confirmación</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Quick Price Change */}
                  {!showQuickPrice ? (
                    <Button variant="outline" className="w-full justify-start" onClick={() => setShowQuickPrice(true)}>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Cambiar Precio
                    </Button>
                  ) : (
                    <div className="space-y-2 p-3 border rounded-lg bg-muted/50">
                      <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona habitación" />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms.map(room => (
                            <SelectItem key={room.id} value={room.id}>
                              {room.name} (€{room.current_price || room.base_price})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        type="number" 
                        placeholder="Nuevo precio €" 
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleApplyPriceChange}>Proponer</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setShowQuickPrice(false); setSelectedRoom(""); setNewPrice(""); }}>Cancelar</Button>
                      </div>
                    </div>
                  )}

                  {/* Quick Rule Toggle */}
                  {!showQuickRule ? (
                    <Button variant="outline" className="w-full justify-start" onClick={() => setShowQuickRule(true)}>
                      <ToggleLeft className="w-4 h-4 mr-2" />
                      Activar/Desactivar Regla
                    </Button>
                  ) : (
                    <div className="space-y-2 p-3 border rounded-lg bg-muted/50">
                      <Select value={selectedRule} onValueChange={setSelectedRule}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona regla" />
                        </SelectTrigger>
                        <SelectContent>
                          {rules.map(rule => (
                            <SelectItem key={rule.id} value={rule.id}>
                              {rule.name} ({rule.is_active ? "Activa" : "Inactiva"})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleApplyRuleToggle}>Proponer</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setShowQuickRule(false); setSelectedRule(""); }}>Cancelar</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Consultas Rápidas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    "¿Cómo está la ocupación?",
                    "Revisa las reglas de pricing",
                    "¿Hay eventos próximos?",
                    "Analiza los precios actuales"
                  ].map((question) => (
                    <Button
                      key={`quick-${question.slice(0, 15)}`}
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-left h-auto py-2"
                      onClick={() => setChatInput(question)}
                    >
                      {question}
                    </Button>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Manual Notes Tab */}
        <TabsContent value="notes">
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Nueva Nota
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Categoría..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descripción..."
                    rows={4}
                  />
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Guardar
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Historial</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {entries.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay notas aún</p>
                  </div>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto">
                    {entries.map(entry => (
                      <ChangeLogEntry key={entry.id} entry={entry} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Import Oscar Tab */}
        <TabsContent value="import">
          <OscarImporter />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ChangeLog;
